import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  ArrowLeft, Receipt, CreditCard, Crown, Download, ExternalLink,
  CheckCircle, Clock, XCircle, ShoppingBag, Sparkles, Calendar
} from 'lucide-react';
import { PlexataIcon } from '../components/PlexataLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const styles = {
    paid: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    pending: 'bg-amber-50 text-amber-600 border-amber-200',
    failed: 'bg-red-50 text-red-600 border-red-200',
    expired: 'bg-gray-50 text-gray-500 border-gray-200',
    unknown: 'bg-gray-50 text-gray-500 border-gray-200',
  };
  
  const icons = {
    paid: CheckCircle,
    pending: Clock,
    failed: XCircle,
    expired: XCircle,
    unknown: Clock,
  };
  
  const Icon = icons[status] || Clock;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.unknown}`}>
      <Icon className="w-3.5 h-3.5" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const InvoiceRow = ({ invoice }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatAmount = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  return (
    <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0 hover:bg-gray-50 -mx-4 px-4 transition-colors" data-testid={`invoice-${invoice.id}`}>
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${invoice.type === 'subscription' ? 'bg-indigo-50' : 'bg-amber-50'}`}>
          {invoice.type === 'subscription' ? (
            <Sparkles className="w-5 h-5 text-indigo-500" />
          ) : (
            <ShoppingBag className="w-5 h-5 text-amber-500" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">{invoice.description}</p>
          <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
            <Calendar className="w-3 h-3" />
            {formatDate(invoice.date)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <StatusBadge status={invoice.status} />
        <span className="text-sm font-semibold text-gray-900 min-w-[80px] text-right">
          {formatAmount(invoice.amount, invoice.currency)}
        </span>
      </div>
    </div>
  );
};

const Billing = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadBillingHistory = async () => {
      try {
        const res = await axios.get(`${API}/payments/billing/history`);
        setBillingData(res.data);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login');
        } else {
          setError('Failed to load billing history');
        }
      } finally {
        setLoading(false);
      }
    };
    loadBillingHistory();
  }, [navigate]);

  const formatAmount = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="billing-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Link to="/dashboard" className="flex items-center gap-2">
              <PlexataIcon size={24} />
              <span className="font-heading text-lg font-medium tracking-tight text-gray-900">plexata</span>
            </Link>
          </div>
          <button 
            onClick={() => { logout(); navigate('/'); }}
            className="text-xs font-medium text-gray-400 hover:text-red-500 transition-colors"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-heading font-medium text-gray-900">Billing & Invoices</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your subscription and view payment history</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-100 text-red-600 text-sm px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {/* Current Plan Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid="current-plan-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Current Plan</p>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${billingData?.current_plan === 'pro' ? 'bg-gradient-to-br from-amber-400 to-orange-500' : 'bg-gray-100'}`}>
                  <Crown className={`w-6 h-6 ${billingData?.current_plan === 'pro' ? 'text-white' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {billingData?.current_plan === 'pro' ? 'Pro Plan' : 'Free Plan'}
                  </h2>
                  {billingData?.plan_package && (
                    <p className="text-sm text-gray-500">
                      {billingData.plan_package === 'pro_monthly' ? 'Billed monthly' : 'Billed annually'}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {billingData?.current_plan === 'free' && (
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-black transition-colors flex items-center gap-2"
                data-testid="upgrade-btn"
              >
                <Sparkles className="w-4 h-4" /> Upgrade to Pro
              </button>
            )}
          </div>
          
          {billingData?.current_plan === 'pro' && billingData?.plan_updated_at && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                Pro since {new Date(billingData.plan_updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium">Total Spent</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatAmount(billingData?.total_spent || 0)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-indigo-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium">Total Invoices</p>
                <p className="text-lg font-semibold text-gray-900">
                  {billingData?.invoices?.length || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium">Products</p>
                <p className="text-lg font-semibold text-gray-900">
                  {billingData?.invoices?.filter(i => i.type === 'product').length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice History */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="invoice-history">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Payment History</h3>
            {billingData?.invoices?.length > 0 && (
              <span className="text-xs text-gray-400">{billingData.invoices.length} transactions</span>
            )}
          </div>
          
          <div className="px-6 py-2">
            {billingData?.invoices?.length > 0 ? (
              billingData.invoices.map((invoice, idx) => (
                <InvoiceRow key={invoice.id || idx} invoice={invoice} />
              ))
            ) : (
              <div className="py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Receipt className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-sm text-gray-500">No payment history yet</p>
                <p className="text-xs text-gray-400 mt-1">Your invoices will appear here after your first purchase</p>
              </div>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">
            Need help with billing? Contact us at{' '}
            <a href="mailto:support@plexata.com" className="text-indigo-600 hover:text-indigo-700 font-medium">
              support@plexata.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Billing;
